import { GlobalState } from '../../../store/global';
import { createSelector } from '@ngrx/store';
import { CampaignFeatureState } from './campaign.state';
import { campaignsAdapter, platformsAdapter } from './campaign-adapters';
import { cleanStringForComparison } from '../../../core/utilities';
import { MediaPlatformStatus, MediaPlatformType } from '../../../models/campaigns-models/media-platform-account';
import { Dictionary } from '@ngrx/entity';


export const selectCampaignState = (state: GlobalState) => state.campaign;


export const selectCampaignsState = createSelector(
    selectCampaignState,
    (state: CampaignFeatureState) => state.campaigns,
);

export const selectValidationRules = createSelector(
    selectCampaignState,
    (state: CampaignFeatureState) => state.validationRules,
);

const selectPlatformsState = createSelector(
    selectCampaignState,
    (state: CampaignFeatureState) => state.platforms,
);

export const { selectAll: selectAllPlatforms } =
    platformsAdapter.getSelectors(selectPlatformsState);

export const selectConnectedFacebookAccounts = createSelector(
    selectAllPlatforms, (platforms) => platforms.filter(mediaPlatform =>
        mediaPlatform.status === MediaPlatformStatus.CONNECTED &&
        mediaPlatform.platform === MediaPlatformType.FACEBOOK),
);

export const selectConnectedXAccounts = createSelector(
    selectAllPlatforms, (platforms) => platforms.filter(mediaPlatform =>
        mediaPlatform.status === MediaPlatformStatus.CONNECTED &&
        mediaPlatform.platform === MediaPlatformType.X),
);

export const selectConnectedInstagramAccounts = createSelector(
    selectAllPlatforms, (platforms) => platforms.filter(mediaPlatform =>
        mediaPlatform.status === MediaPlatformStatus.CONNECTED &&
        mediaPlatform.platform === MediaPlatformType.INSTAGRAM),
);

export const selectConnectedLinkedInAccounts = createSelector(
    selectAllPlatforms, (platforms) => platforms.filter(mediaPlatform =>
        mediaPlatform.status === MediaPlatformStatus.CONNECTED &&
        mediaPlatform.platform === MediaPlatformType.LINKEDIN),
);

export const selectAvailableTokens = createSelector(
    selectCampaignState,
    (state: CampaignFeatureState) => state.clientTokens?.availableTokens,
);

export const { selectAll: selectAllCampaigns } =
    campaignsAdapter.getSelectors(selectCampaignsState);

export const selectCampaignFilters = createSelector(
    selectCampaignState,
    (state: CampaignFeatureState) => state.filters,
);

export const selectCampaignFiltersActive = createSelector(
    selectCampaignFilters,
    (filters) => {
      if (filters) {
        const filterKeys: string[] = Object
            .keys(filters)
            .filter((key: string) => key !== 'sortType'); // don't count sortType as filter
        return filterKeys.some((key: string) => Array.isArray(filters[key]) ? !!filters[key].length : !!filters[key]);
      } else return false;
    },
);

export const selectCampaignTitleFilter = createSelector(
    selectCampaignFilters,
    (filters) => filters.title,
);

// FE filtering by campaign title - other filters trigger an API call
export const selectFilteredCampaigns = createSelector(
    selectAllCampaigns,
    selectCampaignTitleFilter,
    (campaigns, titleFilter) => campaigns?.filter(campaign => {
      return cleanStringForComparison(campaign.title)?.includes(cleanStringForComparison(titleFilter));
    }),
);

export const selectCampaignsTotal = createSelector(
    selectFilteredCampaigns,
    (campaigns) => campaigns?.length,
);

export const selectFocusedCampaign = createSelector(
    selectFilteredCampaigns,
    (state: any, props: { focusedCampaignId: string }) => props.focusedCampaignId,
    (campaigns, focusedCampaignId) => campaigns.find(campaign => campaign.id === focusedCampaignId),
);

export const selectCampaignPlatformTypes = createSelector(
    selectAllPlatforms,
    selectFilteredCampaigns,
    (platforms, campaigns) => {
      const dict: Dictionary<MediaPlatformType[]> = {};
      if (!platforms?.length || !campaigns?.length) return dict;

      const platformMap = new Map<string, MediaPlatformType>();
      platforms.forEach(p => {
        if (p.id && p.platform) {
          platformMap.set(p.id, p.platform);
        }
      });

      campaigns.forEach(campaign => {
        const types: MediaPlatformType[] = [];
        if (campaign.socialMediaAccountIds) {
          campaign.socialMediaAccountIds.forEach(id => {
            const type = platformMap.get(id);
            if (type && !types.includes(type)) {
              types.push(type);
            }
          });
        }
        dict[campaign.id] = types;
      });

      return dict;
    },
);